import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, AlertTriangle, CalendarClock } from "lucide-react";
import { S, MANROPE, FOCUS, API, auDate } from "./lib";

const ddmon = (iso) => auDate(iso);

const Bar = ({ label, done, total }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[10.5px] mb-0.5">
        <span className="font-bold uppercase tracking-[0.08em]" style={{ color: S.ink3 }}>{label}</span>
        <span className="mono" style={{ color: pct === 100 ? S.good : S.ink2 }}>{done}/{total}</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: S.divider }}>
        <div className="h-full transition-[width] duration-500"
          style={{ width: `${pct}%`, background: pct === 100 ? S.good : S.accentEdge }} />
      </div>
    </div>
  );
};

export default function SocialCampaigns({ onOpenCampaign }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    axios.get(`${API}/campaigns`)
      .then((r) => setRows(r.data.campaigns || []))
      .catch(() => { toast.error("Could not load the campaigns"); setRows([]); });
  }, []);

  if (rows === null) {
    return <div className="px-4 py-10 text-[13px]" style={{ color: S.ink2 }}>Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="px-4 py-10 text-[13px]" style={{ color: S.ink2 }} data-testid="social-campaigns-empty">
        No campaigns yet. Tag a few posts with the same campaign name and the pack appears here.
      </div>
    );
  }

  return (
    <div className="p-3.5 grid gap-3" data-testid="social-campaigns"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))" }}>
      {rows.map((c) => (
        <button key={c.name} onClick={() => onOpenCampaign?.(c.name)} data-testid={`social-campaign-${c.name}`}
          className={`text-left rounded-[10px] p-3.5 space-y-3 transition-colors hover:bg-[#FBFAF7] ${FOCUS}`}
          style={{ background: S.card, border: `1px solid ${S.border}` }}>
          <div className="flex items-start gap-2">
            <Megaphone className="w-4 h-4 mt-0.5 shrink-0" style={{ color: S.accentInk }} />
            <div className="min-w-0">
              <div className="truncate" style={{ ...MANROPE, fontSize: 14.5, fontWeight: 800, color: S.ink }}>
                {c.name}
              </div>
              <div className="text-[11.5px]" style={{ color: S.ink2 }}>
                {c.posts} post{c.posts > 1 ? "s" : ""} · {ddmon(c.first_date)} – {ddmon(c.last_date)}
                {c.product_labels?.length ? ` · ${c.product_labels.join(", ")}` : ""}
              </div>
            </div>
            <span className="ml-auto mono text-[17px] font-bold shrink-0"
              style={{ color: c.readiness === 100 ? S.good : S.accentInk }}
              data-testid={`social-campaign-readiness-${c.name}`}>{c.readiness}%</span>
          </div>

          <div className="space-y-1.5">
            <Bar label="Copy" done={c.copy_done} total={c.posts} />
            <Bar label="Visual" done={c.visual_done} total={c.posts} />
            <Bar label="Reel" done={c.reel_done} total={c.posts} />
            <Bar label="Approved" done={c.approved} total={c.posts} />
          </div>

          <div className="flex items-center gap-2.5 flex-wrap text-[11px]">
            <span className="mono px-1.5 py-0.5 rounded" style={{ background: S.panel, color: S.ink2 }}>
              {c.published} published
            </span>
            {c.overdue > 0 && (
              <span className="inline-flex items-center gap-1 font-bold" style={{ color: S.bad }}>
                <AlertTriangle className="w-3 h-3" /> {c.overdue} late
              </span>
            )}
            {c.consent_gate > 0 && (
              <span className="inline-flex items-center gap-1" style={{ color: "#8E3A12" }}>
                <AlertTriangle className="w-3 h-3" /> {c.consent_gate} waiting on consent
              </span>
            )}
            {c.unscheduled > 0 && (
              <span className="inline-flex items-center gap-1" style={{ color: S.ink3 }}>
                <CalendarClock className="w-3 h-3" /> {c.unscheduled} undated
              </span>
            )}
          </div>
          {c.owners?.length > 0 && (
            <div className="text-[11px]" style={{ color: S.ink3 }}>Owned by {c.owners.join(", ")}</div>
          )}
        </button>
      ))}
    </div>
  );
}
